// App.tsx - Updated with Energent.ai colors
import { motion } from "framer-motion";
import React, { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Toaster } from "sonner";

import ChatInterface from "@/components/chat-interface";
import Header from "@/components/header";
import FloatingHelpWidget from "@/components/FloatingHelpWidget";
import { useAppStore } from "@/store/useAppStore";

// Error Fallback Component
const ErrorFallback = ({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="min-h-screen flex items-center justify-center bg-black p-4"
  >
    <div className="max-w-md w-full bg-red-500/10 border border-red-500/20 rounded-2xl p-6 text-center backdrop-blur-sm">
      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
        <span className="text-red-400 text-xl">⚠️</span>
      </div>
      <h2 className="text-lg font-semibold text-white mb-2">
        Something went wrong
      </h2>
      <p className="text-gray-400 text-sm mb-4">{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
      >
        Try again
      </button>
    </div>
  </motion.div>
);

// Background Component
const AnimatedBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    {/* Gradient Background */}
    <div className="absolute inset-0 bg-black" />

    {/* Animated Orbs */}
    <motion.div
      animate={{
        x: [0, 100, 0],
        y: [0, -100, 0],
        scale: [1, 1.2, 1],
      }}
      transition={{
        duration: 20,
        repeat: Infinity,
        ease: "easeInOut",
      }}
      className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-blue-500/20 to-cyan-400/20 rounded-full blur-3xl"
    />

    <motion.div
      animate={{
        x: [0, -120, 0],
        y: [0, 80, 0],
        scale: [1, 0.8, 1],
      }}
      transition={{
        duration: 25,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 5,
      }}
      className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-gradient-to-r from-cyan-400/20 to-blue-600/20 rounded-full blur-3xl"
    />

    {/* Grid Pattern */}
    <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
  </div>
);

// Loading Component
const LoadingSpinner = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="min-h-screen flex items-center justify-center bg-black"
  >
    <div className="text-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        className="w-12 h-12 mx-auto mb-4 border-4 border-blue-500/30 border-t-blue-500 rounded-full"
      />
      <p className="text-gray-400">Loading Voice AI Assistant...</p>
    </div>
  </motion.div>
);

// Main App Component
const ChatPage: React.FC = () => {
  const settings = useAppStore((state) => state.settings);
  const [isLoading, setIsLoading] = React.useState(true);

  // Configuration for help widget placement
  const [helpWidgetMode] = React.useState<"floating" | "header" | "both">(
    "floating"
  );

  // Initialize app
  useEffect(() => {
    // Simulate app initialization
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Show loading screen during initialization
  if (isLoading) return <LoadingSpinner />;

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="min-h-screen bg-black text-white relative">
        <AnimatedBackground />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 container mx-auto px-4 py-8"
        >
          {/* Header includes explainer when helpWidgetMode is 'header' or 'both' */}
          <Header />

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <ChatInterface />
          </motion.div>
        </motion.div>

        {/* Floating Help Widget - only show when mode includes 'floating' */}
        {(helpWidgetMode === "floating" || helpWidgetMode === "both") && (
          <FloatingHelpWidget />
        )}

        {/* Toast Notifications */}
        <Toaster
          theme={settings.theme}
          position="bottom-right"
          expand={true}
          richColors
          toastOptions={{
            style: {
              background: settings.theme === "dark" ? "#1f2937" : "#ffffff",
              color: settings.theme === "dark" ? "#f1f5f9" : "#0f172a",
              border: `1px solid ${
                settings.theme === "dark" ? "#475569" : "#e2e8f0"
              }`,
            },
          }}
        />
      </div>
    </ErrorBoundary>
  );
};

export default ChatPage;